import React, { useEffect, useState } from 'react';
import { api, type AdPurchase, type InterviewSession, type LodgingBooking, type MarkAdmission } from '../api';
import { TYPE, useTheme } from '../theme';
import { Badge, Button, Card, SectionHeader, type BadgeTone } from '../components/ui';
import { admissionTone, myAdmissionText } from '../admission/admissionText';
import { InterviewCall } from '../admission/InterviewCall';
import { isFreePurchase, kronor, purchaseStatusText } from './jakt/adPurchase';
import { guestsText, lodgingStatusText, stayText } from './jakt/lodging';
import { LodgingInstructions, hasLodgingInfo } from './jakt/LodgingInfo';

const TONE: Record<'wait' | 'ok' | 'off' | 'bad', BadgeTone> = { wait: 'accent', ok: 'ok', off: 'neutral', bad: 'danger' };

/** "Mina köp" under Min Jakt: status för köpta annonser och bokat boende, så att jägaren ser när en reservation släppts. */
export function MyPurchases() {
  const T = useTheme();
  const [list, setList] = useState<AdPurchase[]>([]);
  const [stays, setStays] = useState<LodgingBooking[]>([]);
  // P2/P3: antagningen per köp (intervju, avtal att signera, i gruppen).
  const [admissions, setAdmissions] = useState<MarkAdmission[]>([]);
  const [call, setCall] = useState<InterviewSession | null>(null);
  const [callErr, setCallErr] = useState('');
  useEffect(() => {
    api.myAdmissions().then(setAdmissions).catch(() => {});
    api.myAdPurchases().then(setList).catch(() => {});
    api.myLodgingBookings().then(setStays).catch(() => {});
  }, []);
  // Boende som köpts med en annons visas under annonsens köp.
  const ownStays = stays.filter(b => !b.adPurchaseId);
  const stayOf = (p: AdPurchase) => (p.lodgingBookingId ? stays.find(b => b.id === p.lodgingBookingId) : undefined);
  if (!list.length && !ownStays.length) return null;
  const row = (key: string, first: boolean, title: string, sub: string, badge: React.ReactNode) => (
    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', minHeight: 48, borderTop: first ? 'none' : `1px solid ${T.border}` }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</div>
        <div style={{ ...TYPE.small, color: T.textMuted, marginTop: 2 }}>{sub}</div>
      </div>
      {badge}
    </div>
  );
  return (
    <section style={{ marginTop: 34 }} data-testid="my-purchases">
      <SectionHeader title="Mina köp" />
      <Card padding={0} style={{ padding: '4px 18px' }}>
        {list.map((p, i) => {
          const s = purchaseStatusText(p.status, p.decisionNote, isFreePurchase(p));
          const st = stayOf(p);
          const adm = admissions.find(a => a.id === p.id);
          const at = adm ? myAdmissionText({ ...adm, amount: p.amount }) : null;
          return <React.Fragment key={p.id}>
            {row(p.id, i === 0, `${p.adTitle || 'Annons'}${p.markName ? ` · ${p.markName}` : ''}`,
              `${isFreePurchase(p) ? 'Ansökan' : kronor(p.amount)}${p.dateStart ? ` · ${p.dateStart}` : ''}${st ? ` · inkl. boende ${st.lodgingName ?? ''} ${stayText(st.checkIn, st.checkOut)}` : ''} · ${at ? at.detail : s.detail}`,
              adm && at ? <Badge tone={admissionTone(adm.stage)}>{at.label}</Badge> : <Badge tone={TONE[s.tone]}>{s.label}</Badge>)}
            {adm && at?.action === 'sign' && (
              <div style={{ paddingBottom: 12 }}>
                <Button data-testid="my-sign-contract" onClick={() => { window.location.href = `/avtal/${encodeURIComponent(adm.signingToken!)}`; }}>Läs och signera avtalet</Button>
              </div>
            )}
            {adm && at?.action === 'interview' && (
              <div style={{ paddingBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button variant="secondary" onClick={() => { setCallErr(''); api.joinAdmissionInterview(adm.id).then(setCall).catch(e => setCallErr(e.message)); }}>Gå med i intervjun</Button>
                {callErr && <span role="alert" style={{ ...TYPE.small, color: T.danger }}>{callErr}</span>}
              </div>
            )}
            {st && hasLodgingInfo(st) && <div style={{ paddingBottom: 12 }}><LodgingInstructions info={st} /></div>}
          </React.Fragment>;
        })}
        {ownStays.map((b, i) => {
          const s = lodgingStatusText(b.status);
          return <React.Fragment key={b.id}>
            {row(b.id, !list.length && i === 0, `Boende: ${b.lodgingName ?? 'boende'}${b.markName ? ` · ${b.markName}` : ''}`,
              `${kronor(b.amount)} · ${stayText(b.checkIn, b.checkOut)} · ${guestsText(b.guests)}`,
              <Badge tone={TONE[s.tone]}>{s.label}</Badge>)}
            {hasLodgingInfo(b) && <div style={{ paddingBottom: 12 }}><LodgingInstructions info={b} /></div>}
          </React.Fragment>;
        })}
      </Card>
      {call && <InterviewCall session={call} onClose={() => setCall(null)} />}
    </section>
  );
}
